import { useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Tooltip } from '@vd/ui';
import styles from './HoverTip.module.css';

/**
 * A tooltip that follows the pointer over a chart's marks (Jason's review of
 * 16 Oct): a bar's part, a week, a page's meter. Mouse only: every chart on
 * Reports has a table twin with the same figures for a screen reader, so the
 * marks themselves stay hidden from it. Drawn fixed to the window, so a
 * scrolling pane never clips it, and placed by its own measured size before
 * it paints (`placeTip`): above and to the right of the pointer, flipped to
 * the left or below where there is no room, and never past the window's edge.
 *
 *   const tip = useHoverTip();
 *   <span {...tip.bind('Bug: 4 of 6 (67%)')} />
 *   {tip.node}
 */
export function useHoverTip() {
  const [tip, setTip] = useState<{ content: ReactNode; x: number; y: number } | null>(null);
  const bind = (content: ReactNode) => ({
    onMouseMove: (event: MouseEvent) => setTip({ content, x: event.clientX, y: event.clientY }),
    onMouseLeave: () => setTip(null),
  });
  const box = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = box.current;
    if (!tip || !element) return;
    const place = placeTip(tip, { width: element.offsetWidth, height: element.offsetHeight }, { width: window.innerWidth, height: window.innerHeight });
    element.style.left = `${place.left}px`;
    element.style.top = `${place.top}px`;
    element.style.visibility = 'visible';
  }, [tip]);
  // Hidden until placed, so it never paints a frame where it does not fit.
  const node = tip
    ? <div ref={box} className={styles.tip} role="tooltip" data-testid="chart-tip" style={{ left: 0, top: 0, visibility: 'hidden' }}>{tip.content}</div>
    : null;
  return { bind, node };
}

/** How far a tip sits from the pointer, and how far it keeps from the window's edges. */
const TIP_GAP = { x: 12, y: 10 };
const TIP_MARGIN = 8;

/**
 * Where a tip of `size` goes for a pointer at `at` in a window of `view`:
 * above and to the right, flipped to the left when its right edge would leave
 * the window and below when its top would, then held inside the margin on
 * every side, so a tip near a pane's right edge stays on the page.
 */
export function placeTip(at: { x: number; y: number }, size: { width: number; height: number }, view: { width: number; height: number }) {
  let left = at.x + TIP_GAP.x;
  if (left + size.width > view.width - TIP_MARGIN) left = at.x - TIP_GAP.x - size.width;
  let top = at.y - TIP_GAP.y - size.height;
  if (top < TIP_MARGIN) top = at.y + TIP_GAP.y + 8;
  const clamp = (value: number, room: number) => Math.max(TIP_MARGIN, Math.min(value, room - TIP_MARGIN));
  return { left: clamp(left, view.width - size.width), top: clamp(top, view.height - size.height) };
}

/** An info bubble beside a heading: what the section shows and how it is counted, on hover or focus. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip content={children} multiline placement="bottom">
      <button type="button" className={styles.info} aria-label={`About ${label}`} data-testid="info-tip">
        <Info size={14} aria-hidden="true" />
      </button>
    </Tooltip>
  );
}
